import { StylesProvider } from '@material-ui/core/styles';
import { LottieComponentProps } from 'lottie-react';
import React, { useCallback, useEffect, useState } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import './App.css';
import StartupAnimation from './common/components/startup-animation/startup-animation';
import { LoadingSpinnerModal } from './common/components/with-spinner/with-spinner';
import XLRLogger from './common/helpers/logger';
import * as messaging from './common/helpers/message-id-helper';
import { getLaunchAnimation } from './common/helpers/startup-animation-helper';
import { store } from './common/redux/configure-store';
import { DeviceStatusActions } from './common/redux/reducers/device-status-reducer';
import { TouchpointActions } from './common/redux/reducers/touchpoint-reducer';
import { onDeviceMessage } from './devices/rds-connection';
import { enableScanner } from './devices/scanner-operations';
import { EmeraldThemeProvider } from './emerald-theme-provider';
import ErrorBoundary from './error-boundary/error-boundary';
import { ResponsiveProvider } from './home-page/hooks/responsive-context';
import { requestPOSServiceType } from './xlr-configuration/business-logic/helpers/pos-service-type-request';
import ServiceTypeLoadError from './xlr-configuration/pos-service-type-error';
import {
  getBrowserTitle,
  getMetaData,
  getXlrConfig,
  ServiceTypeContextProvider,
  setAndSealMetadata,
} from './xlr-configuration/pos-service-type-utils';
import { ServiceType } from './xlr-configuration/service-type';
import { XLRPos } from './xlr-configuration/xlr-pos';
import { getCurrentXLRConfig } from './xlr-configuration/xlr-config';
import { terminalTlog } from './terminal-tlog';
import { TlogTransactionType } from './transaction-search/interfaces/transaction-search-response';

export const setupCypress = () => {
  if (!window.Cypress) {
    return;
  }

  // @ts-ignore
  window.store = store;

  // @ts-ignore
  // rds-connection test
  window.onDeviceMessage = (data: { data: string }) => {
    enableScanner();
    onDeviceMessage(data);
  };

  // @ts-ignore
  window.addDevice = device => {
    store.dispatch(DeviceStatusActions.addDevice(device));
  };

  // @ts-ignore
  window.updateDevice = device => {
    store.dispatch(DeviceStatusActions.updateDevice(device));
  };
  // @ts-ignore
  window.setTouchpointConfig = tpConfig => {
    store.dispatch(TouchpointActions.setTouchpointConfig(tpConfig));
  };
};

setupCypress();

window.onunhandledrejection = err => {
  XLRLogger.error({ message: err.reason, source: 'Unhandled error in application.' });
};

const App: React.FC = (): JSX.Element => {
  const [serviceTypeLoaded, setServiceTypeLoaded] = useState(false);

  useEffect(() => {
    if (serviceTypeLoaded) {
      const { callTlogTerminal } = getCurrentXLRConfig();
      if (callTlogTerminal) {
        terminalTlog(TlogTransactionType.TERMINAL_UP);

        window.addEventListener('beforeunload', function(e) {
          e.preventDefault();
          terminalTlog(TlogTransactionType.TERMINAL_DOWN);
        });
      }
    }
  }, [serviceTypeLoaded]);

  const defaultOptions: LottieComponentProps = {
    loop: false,
    autoplay: false,
    animationData: getLaunchAnimation(getMetaData().serviceType),
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  const onStart = (): void => {
    XLRLogger.debug({
      message: 'Winston logger ready.',
      source: 'App.tsx',
      data: { ready: true },
    });
    messaging.setMessageId();
  };

  const [error, setError] = useState(false);

  const runServiceTypeCall = useCallback((): Promise<ServiceType> | undefined => {
    const retryDelay = 1000;
    const remainingTries = 14;
    const serviceTypeCall = requestPOSServiceType(retryDelay, remainingTries);
    serviceTypeCall.catch(err => {
      XLRLogger.error({
        message: err,
        source: 'App.tsx',
      });
      setError(true);
    });
    const xlrConfigCall = getXlrConfig();
    Promise.all([xlrConfigCall, serviceTypeCall])
      .then(([xlrConfig, type]) => {
        XLRLogger.info({
          message: `Running in '${type}' mode`,
          source: 'index.tsx',
        });
        setAndSealMetadata(type, xlrConfig);
        setServiceTypeLoaded(true);
        setError(false);
        document.title = getBrowserTitle();
      })
      .catch(() => {
        setError(true);
      });
    return serviceTypeCall;
  }, []);

  useEffect((): void => {
    if (!error) {
      onStart();
      runServiceTypeCall();
    }
  }, [error, runServiceTypeCall]);

  return (
    <ReduxProvider store={store}>
      <EmeraldThemeProvider>
        <StylesProvider injectFirst>
          <ResponsiveProvider>
            <ErrorBoundary>
              {serviceTypeLoaded ? (
                <ServiceTypeContextProvider>
                  <XLRPos />
                </ServiceTypeContextProvider>
              ) : (
                <>
                  <StartupAnimation lottieProps={defaultOptions} />;
                  <LoadingSpinnerModal />
                </>
              )}

              {error && <ServiceTypeLoadError />}
            </ErrorBoundary>
          </ResponsiveProvider>
        </StylesProvider>
      </EmeraldThemeProvider>
    </ReduxProvider>
  );
};

export default App;
